import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { PageTitle, StatusBadge } from "@/components/brand";
import { useAuth } from "@/context/AuthContext";

const CreateContest = ({ matches, onDone }) => {
  const [f, setF] = useState({ fixture_id: "", name: "Special Contest", entry_fee: 50, max_entries: 50, prize_type: "top3" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { await api.post("/admin/fantasy/contests", { ...f, fixture_id: Number(f.fixture_id), entry_fee: Number(f.entry_fee), max_entries: Number(f.max_entries) }); toast.success("Contest created"); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="create-contest-form" className="card-gold mb-8 grid gap-4 p-6 md:grid-cols-3">
      <div className="md:col-span-3"><label className="text-sm font-semibold" htmlFor="cc-match">Match</label>
        <select id="cc-match" data-testid="cc-match" value={f.fixture_id} onChange={set("fixture_id")} className="input-cr mt-2"><option value="">Select upcoming match</option>
          {matches.map((m) => <option key={m.id} value={m.id}>{m.home.name} vs {m.away.name} · {dateTime(m.start)}</option>)}</select></div>
      <div><label className="text-sm font-semibold" htmlFor="cc-name">Name</label><input id="cc-name" data-testid="cc-name" value={f.name} onChange={set("name")} className="input-cr mt-2" /></div>
      <div><label className="text-sm font-semibold" htmlFor="cc-fee">Entry fee (coins)</label><input id="cc-fee" data-testid="cc-fee" inputMode="numeric" value={f.entry_fee} onChange={set("entry_fee")} className="input-cr mt-2" /></div>
      <div><label className="text-sm font-semibold" htmlFor="cc-size">Max teams</label><input id="cc-size" data-testid="cc-size" inputMode="numeric" value={f.max_entries} onChange={set("max_entries")} className="input-cr mt-2" /></div>
      <div><label className="text-sm font-semibold" htmlFor="cc-prize">Prize</label>
        <select id="cc-prize" data-testid="cc-prize" value={f.prize_type} onChange={set("prize_type")} className="input-cr mt-2"><option value="top3">Top 3 · 50/30/20%</option><option value="winner">Winner takes all</option></select></div>
      <p className="self-end text-xs text-cr-muted md:col-span-1">10% of entry fees comes to you as commission. The rest is the prize pool.</p>
      <button data-testid="cc-save" disabled={busy || !f.fixture_id} onClick={save} className="btn-gold self-end py-3 normal-case">{busy ? "Creating…" : "Create contest"}</button>
    </div>
  );
};

const RoleEditor = ({ matches }) => {
  const [fid, setFid] = useState("");
  const [players, setPlayers] = useState([]);
  useEffect(() => { if (fid) { setPlayers([]); api.get(`/fantasy/matches/${fid}/squad`).then((r) => setPlayers(r.data.players)); } }, [fid]);
  const change = async (p, role) => {
    try { await api.put(`/admin/fantasy/players/${p.id}/role`, { role }); setPlayers(players.map((x) => (x.id === p.id ? { ...x, role } : x))); toast.success(`${p.name} → ${role}`); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="card-cr mt-10 p-6">
      <h2 className="text-lg font-bold">Player roles</h2><p className="mb-3 text-xs text-cr-muted">Roles are guessed from batting/bowling style. Mark wicket-keepers and all-rounders here.</p>
      <select data-testid="role-match" value={fid} onChange={(e) => setFid(e.target.value)} className="input-cr mb-4"><option value="">Select match</option>{matches.map((m) => <option key={m.id} value={m.id}>{m.home.name} vs {m.away.name}</option>)}</select>
      <div className="grid gap-2 sm:grid-cols-2">{players.map((p) => (
        <div key={p.id} className="flex items-center justify-between rounded border border-cr-line px-3 py-2 text-sm"><span>{p.name} <span className="text-xs text-cr-muted">{p.team}</span></span>
          <select data-testid={`role-${p.id}`} value={p.role} onChange={(e) => change(p, e.target.value)} className="input-cr w-24 py-1 text-xs">{["WK", "BAT", "AR", "BOWL"].map((r) => <option key={r}>{r}</option>)}</select></div>))}</div>
    </div>
  );
};

export default function AdminFantasy() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/fantasy/contests").then((r) => setRows(r.data));
  useEffect(() => { api.get("/fantasy/matches").then((r) => setMatches(r.data)); load(); }, []);
  const cancel = async (c) => {
    if (!window.confirm(`Cancel ${c.name} and refund ${c.entries} entries?`)) return;
    try { await api.post(`/admin/fantasy/contests/${c.id}/cancel`); toast.success("Cancelled and refunded"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="admin-fantasy-page">
      <PageTitle crumbs={["Admin", "Fantasy"]} title="Fantasy Contests" subtitle="3 contests are created automatically for every upcoming match. Add your own special contests too. Contests settle automatically after the match." />
      {user?.role === "admin" && <CreateContest matches={matches} onDone={load} />}
      <div className="card-cr overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="text-left text-xs uppercase text-cr-muted"><tr>{["Contest", "Match", "Entry", "Teams", "Pool", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>{rows.map((c) => (
            <tr key={c.id} data-testid={`admin-contest-${c.id}`} className="border-t border-cr-line">
              <td className="px-4 py-3 font-semibold">{c.name}{c.auto && <span className="ml-1 text-xs text-cr-muted">(auto)</span>}</td>
              <td className="px-4 py-3"><Link to={`/fantasy/contest/${c.id}`} className="hover:text-cr-gold">{c.match}</Link><p className="text-xs text-cr-muted">{dateTime(c.start)}</p></td>
              <td className="px-4 py-3">{c.entry_fee}</td><td className="px-4 py-3">{c.entries}/{c.max_entries}</td><td className="px-4 py-3 text-cr-gold">{c.pool}</td>
              <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
              <td className="px-4 py-3">{["open", "live"].includes(c.status) && <button data-testid={`cancel-contest-${c.id}`} onClick={() => cancel(c)} className="text-xs text-cr-danger">Cancel</button>}</td>
            </tr>))}</tbody>
        </table>
      </div>
      <RoleEditor matches={matches} />
    </div>
  );
}
